import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { DownOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Empty,
  Result,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import pageRoutes, { isErpPermission } from '@routes/pageRoutes.ts';
import type { ErpFunctionalGroup, PageRoute } from '@routes/pageRoutes.ts';
import { useAppSelector } from '@store/store.tsx';
import {
  selectPlanCapabilities,
  selectSiteApps,
  selectSiteFeatureFlags,
} from '@store/slices/settingsSlice.tsx';
import {
  selectIsStaff,
  selectIsSuperuser,
  selectUserGroups,
  selectUserPermissions,
} from '@store/slices/userSlice.tsx';

import '@dashboard/components/DashboardSections.css';
import './ERPOverviewPage.css';
import { logInfo } from '@tools/LogsHandler.tsx';
import {
  buildErpSections,
  getErpRoutes,
  GROUP_COPY,
  getRouteAccent,
  isErpRouteVisible,
} from './erpSections.ts';
import { useErpAdvancedMode } from './useErpAdvancedMode.ts';
import ResponsivePageHeading from '@components/layout/ResponsivePageHeading.tsx';

type CSSVars = CSSProperties & Partial<Record<`--${string}`, string | number>>;

const SECTION_SKELETON_GROUPS = Array.from({ length: 3 });
const SECTION_SKELETON_TILES = Array.from({ length: 4 });

const ERPOverviewPage = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();

  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const features = (useAppSelector(selectSiteFeatureFlags) ?? {}) as Record<string, boolean | undefined>;
  const planCapabilities = useAppSelector(selectPlanCapabilities);
  const isStaff = useAppSelector(selectIsStaff);
  const isSuperuser = useAppSelector(selectIsSuperuser);
  const permissions = useAppSelector(selectUserPermissions) ?? [];
  const groups = useAppSelector(selectUserGroups);
  const userStatus = useAppSelector((state) => state.user.status);

  const [collapsedGroups, setCollapsedGroups] =
    useState<Partial<Record<ErpFunctionalGroup, boolean>>>({});

  const { advancedMode, setAdvancedMode } = useErpAdvancedMode();

  const erpEnabled = useMemo(() => {
    const featureEnabled = typeof features.erp === 'boolean' ? features.erp : true;
    return featureEnabled && siteApps.includes('erp');
  }, [features.erp, siteApps]);

  const isInitialLoading = userStatus === 'loading';

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);
  const hasErpPermission = useMemo(
    () => isSuperuser || permissions.some((permission) => isErpPermission(permission)),
    [permissions, isSuperuser],
  );

  const accessContext = useMemo(
    () => ({
      erpEnabled,
      isStaff,
      isSuperuser,
      hasErpPermission,
      permissionSet,
      groups,
      siteApps,
      features,
      planCapabilities,
    }),
    [erpEnabled, features, groups, hasErpPermission, isStaff, isSuperuser, permissionSet, planCapabilities, siteApps],
  );

  const tileBaseStyles = useMemo<CSSVars>(
    () => ({
      width: '100%',
      minHeight: 132,
      height: '100%',
      padding: 18,
      borderRadius: 18,
      border: '1px solid transparent',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-center',
      gap: 16,
      background: token.colorBgElevated ?? token.colorBgContainer,
      color: token.colorText,
      textAlign: 'center',
      cursor: 'pointer',
      boxShadow:
        token.boxShadowTertiary ?? token.boxShadowSecondary ?? '0 18px 36px rgba(15, 23, 42, 0.08)',
      touchAction: 'manipulation',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      '--dashboard-tile-focus': token.colorPrimary,
      '--dashboard-tile-border': token.colorBorderSecondary,
      '--dashboard-tile-bg': token.colorBgElevated ?? token.colorBgContainer,
    }),
    [
      token.boxShadowSecondary,
      token.boxShadowTertiary,
      token.colorBgContainer,
      token.colorBgElevated,
      token.colorBorderSecondary,
      token.colorPrimary,
      token.colorText,
    ],
  );

  const iconStyles = useMemo<CSSProperties>(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'clamp(3.25rem, 22vw, 3.75rem)',
      height: 'clamp(3.25rem, 22vw, 3.75rem)',
      borderRadius: 18,
      fontSize: 'clamp(1.75rem, 6vw, 2.25rem)',
      flexShrink: 0,
    }),
    [],
  );

  const sections = useMemo(
    () => {
      logInfo('***** sections *****', {
        siteApps,
        featuresErp: features.erp,
        isStaff,
        permissions,
        hasErpPermission,
        advancedMode,
      });

      return buildErpSections({
        routes: pageRoutes,
        ...accessContext,
        advancedMode,
        groups,
      });
    },
    [
      accessContext,
      advancedMode,
      groups,
      permissions,
    ],
  );

  const allErpRoutes = useMemo(() => getErpRoutes(pageRoutes), []);

  const hasAdvancedRoutes = useMemo(() => {
    return allErpRoutes.some(
      (route) => route.advancedMode && isErpRouteVisible(route, accessContext),
    );
  }, [accessContext, allErpRoutes]);

  const availableWithMorePermissions = useMemo(() => {
    return allErpRoutes.filter((route) => {
      if (!route.requiredPermissions?.length || route.featureFlag && !features[route.featureFlag]) {
        return false;
      }

      const visibleForPlan = isErpRouteVisible(route, {
        ...accessContext,
        isSuperuser: true,
        hasErpPermission: true,
        permissionSet: new Set(route.requiredPermissions ?? []),
      });
      if (!visibleForPlan) {
        return false;
      }

      if (!advancedMode && route.advancedMode) {
        return false;
      }

      return !isSuperuser && !route.requiredPermissions.some((permission) => permissionSet.has(permission));
    });
  }, [accessContext, advancedMode, allErpRoutes, features, isSuperuser, permissionSet]);

  const missingPermissionGroups = useMemo(() => {
    return Array.from(
      new Set(
        availableWithMorePermissions.map((route) => GROUP_COPY[route.group ?? 'overview']?.title ?? route.group ?? ''),
      ),
    ).filter(Boolean);
  }, [availableWithMorePermissions]);

  const missingPermissions = useMemo(() => {
    return Array.from(
      new Set(availableWithMorePermissions.flatMap((route) => route.requiredPermissions ?? [])),
    );
  }, [availableWithMorePermissions]);

  const disabledAppGroups = useMemo(() => {
    const groups = allErpRoutes.filter((route) => {
      if (!route.requiredApps?.length || route.featureFlag && !features[route.featureFlag]) {
        return false;
      }

      return !isErpRouteVisible(route, {
        ...accessContext,
        isSuperuser: true,
        hasErpPermission: true,
        permissionSet: new Set(route.requiredPermissions ?? []),
      });
    });

    return Array.from(
      new Set(groups.map((route) => GROUP_COPY[route.group ?? 'overview']?.title ?? route.group ?? '')),
    ).filter(Boolean);
  }, [accessContext, allErpRoutes, features]);

  const toggleGroup = useCallback((group: ErpFunctionalGroup) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  const handleRequestAccess = useCallback((details?: {
    groups?: string[];
    permissions?: string[];
    disabledGroups?: string[];
    reason?: string;
  }) => {
    if (typeof window === 'undefined') {
      return;
    }

    const bodyLines = [
      'Hola, necesito acceso a módulos del ERP.',
      details?.reason ? `Motivo: ${details.reason}` : null,
      details?.groups?.length ? `Módulos solicitados: ${details.groups.join(', ')}` : null,
      details?.permissions?.length ? `Permisos requeridos: ${details.permissions.join(', ')}` : null,
      details?.disabledGroups?.length
        ? `Módulos deshabilitados para la organización: ${details.disabledGroups.join(', ')}`
        : null,
    ].filter(Boolean);

    const mailto = `mailto:?subject=${encodeURIComponent('Solicitud de acceso ERP')}${bodyLines.length ? `&body=${encodeURIComponent(bodyLines.join('\n'))}` : ''
      }`;

    window.open(mailto, '_blank');
  }, []);

  const getDisabledReason = useCallback(
    (route: PageRoute) => {
      if (route.enabled === false) {
        return 'Este módulo estará disponible próximamente.';
      }

      if (route.featureFlag && !features[route.featureFlag]) {
        return 'Funcionalidad en evaluación. Próximamente.';
      }

      if (!isErpRouteVisible(route, {
        ...accessContext,
        isSuperuser: true,
        hasErpPermission: true,
        permissionSet: new Set(route.requiredPermissions ?? []),
      })) {
        return 'Este módulo no está activo para tu organización.';
      }

      if (route.requiredPermissions?.length && !route.requiredPermissions.some((permission) => permissionSet.has(permission))) {
        return 'Requiere habilitación adicional. Contactá a un administrador.';
      }

      return 'Módulo no disponible.';
    },
    [accessContext, features, permissionSet],
  );

  if (!erpEnabled || !isStaff) {
    const lockReason = !isStaff
      ? 'Tu cuenta no tiene rol de staff habilitado.'
      : 'El módulo ERP no está activo para tu organización.';
    return (
      <div className="erp-overview" data-testid="erp-overview-locked">
        <Result
          status="403"
          title="Acceso restringido"
          subTitle={lockReason}
          extra={
            <Button type="primary" onClick={() => handleRequestAccess({ reason: lockReason })}>
              Solicitar acceso
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="erp-overview" data-testid="erp-overview-page">
      <ResponsivePageHeading
        title="Overview ERP"
        description={(
          <Typography.Text type="secondary">
            {advancedMode ? 'Mostrando todas las pantallas disponibles.' : 'Modo simplificado activo: se ocultan pantallas avanzadas.'}
          </Typography.Text>
        )}
        actions={hasAdvancedRoutes ? (
          <div className="erp-overview__header-actions">
            <Tooltip
              title={
                advancedMode
                  ? 'Mostrando todos los módulos disponibles.'
                  : 'Mostrando solo módulos esenciales.'
              }
            >
              <Space size="small" align="center">
                <Typography.Text type="secondary">Modo avanzado</Typography.Text>
                <Switch
                  checked={advancedMode}
                  onChange={setAdvancedMode}
                  aria-label="Activar modo avanzado"
                  size="small"
                />
              </Space>
            </Tooltip>
          </div>
        ) : null}
        className="erp-overview__header"
      />
      <header className="erp-overview__header" hidden>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography.Title level={2} style={{ margin: 0 }}>
            Overview ERP
          </Typography.Title>
          {/*<Typography.Paragraph type="secondary" style={{ margin: 0, maxWidth: 720 }}>*/}
          {/*  Accedé rápidamente a ventas, inventario, compras y finanzas desde un único panel responsivo.*/}
          {/*</Typography.Paragraph>*/}
          <Typography.Text type="secondary">
            {advancedMode ? 'Mostrando todas las pantallas disponibles.' : 'Modo simplificado activo: se ocultan pantallas avanzadas.'}
          </Typography.Text>
        </div>
        {hasAdvancedRoutes ? (
          <div className="erp-overview__header-actions">
            <Tooltip
              title={
                advancedMode
                  ? 'Mostrando todos los módulos disponibles.'
                  : 'Mostrando solo módulos esenciales.'
              }
            >
              <Space size="small" align="center">
                <Typography.Text type="secondary">Modo avanzado</Typography.Text>
                <Switch
                  checked={advancedMode}
                  onChange={setAdvancedMode}
                  aria-label="Activar modo avanzado"
                  size="small"
                />
              </Space>
            </Tooltip>
          </div>
        ) : null}
      </header>

      <section className="erp-overview__sections" aria-label="Módulos ERP" aria-busy={isInitialLoading}>
        {isInitialLoading ? (
          SECTION_SKELETON_GROUPS.map((_, groupIndex) => (
            <div
              key={`erp-overview-skeleton-group-${groupIndex}`}
              className="erp-overview__group erp-overview__group--skeleton"
            >
              <Skeleton.Input active style={{ width: 180, height: 24 }} />
              <div className="erp-overview__grid">
                {SECTION_SKELETON_TILES.map((__, tileIndex) => (
                  <Skeleton.Button
                    active
                    key={`erp-overview-skeleton-tile-${groupIndex}-${tileIndex}`}
                    style={{ width: '100%', height: 132 }}
                  />
                ))}
              </div>
            </div>
          ))
        ) : sections.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space orientation="vertical" align="center" size={12}>
                <Typography.Text strong>
                  {availableWithMorePermissions.length > 0
                    ? 'Módulos ERP disponibles con permisos adicionales'
                    : 'No hay módulos ERP disponibles'}
                </Typography.Text>
                {availableWithMorePermissions.length > 0 ? (
                  <Typography.Text type="secondary">
                    Con permisos adicionales podrías acceder a: {missingPermissionGroups.join(', ')}.
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">
                    Tu rol actual no tiene acceso a módulos ERP. Contactá a un administrador para solicitar acceso.
                  </Typography.Text>
                )}
                {disabledAppGroups.length > 0 ? (
                  <Alert
                    type="info"
                    showIcon
                    title={`Algunos módulos no están habilitados para tu organización: ${disabledAppGroups.join(', ')}.`}
                  />
                ) : null}
                <Button
                  type="primary"
                  onClick={() =>
                    handleRequestAccess({
                      groups: missingPermissionGroups,
                      permissions: missingPermissions,
                      disabledGroups: disabledAppGroups,
                      reason:
                        availableWithMorePermissions.length > 0
                          ? 'Faltan permisos para módulos ERP.'
                          : 'No hay módulos ERP disponibles.',
                    })
                  }
                >
                  Solicitar acceso
                </Button>
              </Space>
            }
          />
        ) : (
          sections.map((section) => {
            const copy = GROUP_COPY[section.group];
            const isCollapsed = collapsedGroups[section.group] ?? false;
            const headingId = `erp-overview-group-${section.group}`;
            const contentId = `erp-overview-grid-${section.group}`;

            return (
              <div
                key={section.group}
                className="erp-overview__group"
                data-group={section.group}
                data-testid={`erp-overview-section-${section.group}`}
                data-collapsed={isCollapsed ? 'true' : 'false'}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(section.group)}
                  className="erp-overview__group-toggle dashboard-focus-ring"
                  aria-expanded={!isCollapsed}
                  aria-controls={contentId}
                  id={headingId}
                >
                  <div className="erp-overview__group-toggle-row">
                    <h3 className="erp-overview__group-title">{copy.title}</h3>
                    <DownOutlined className="erp-overview__group-caret" aria-hidden="true" />
                  </div>
                  <span className="erp-overview__group-description">{copy.description}</span>
                </button>
                <div
                  className="erp-overview__grid"
                  id={contentId}
                  role="region"
                  aria-labelledby={headingId}
                  hidden={isCollapsed}
                  data-collapsed={isCollapsed ? 'true' : 'false'}
                >
                  {section.routes.map((route) => {
                    const Icon = route.icon;
                    const accent = getRouteAccent(route, token.colorPrimary);
                    const isDisabled = route.enabled === false;
                    const disabledReason = isDisabled ? getDisabledReason(route) : null;
                    const surface =
                      tileBaseStyles.background ?? token.colorBgElevated ?? token.colorBgContainer;
                    const tileStyles: CSSVars = {
                      ...tileBaseStyles,
                      '--dashboard-tile-accent': accent,
                      '--dashboard-tile-border': token.colorBorderSecondary,
                      '--dashboard-tile-shadow': tileBaseStyles.boxShadow ?? token.boxShadowSecondary,
                      '--dashboard-tile-on-accent': token.colorTextLightSolid,
                      '--dashboard-focus-color': accent,
                      '--erp-tile-border': `color-mix(in srgb, ${accent} 28%, ${token.colorBorderSecondary})`,
                      '--erp-tile-surface': surface,
                      '--erp-tile-surface-elevated': `color-mix(in srgb, ${accent} 12%, ${surface})`,
                      '--erp-icon-bg': `color-mix(in srgb, ${accent} 34%, transparent)`,
                      '--erp-icon-shadow': `0 22px 44px color-mix(in srgb, ${accent} 24%, transparent)`,
                      '--erp-icon-color': `var(--web-color-1, ${token.colorTextLightSolid})`,
                      '--erp-tile-hover-shadow': `0 26px 54px color-mix(in srgb, ${accent} 22%, transparent)`,
                      opacity: isDisabled ? 0.72 : 1,
                      cursor: isDisabled ? 'not-allowed' : tileBaseStyles.cursor,
                    };

                    const disabledCopy = isDisabled
                      ? route.featureFlagEnabledCopy ?? 'Módulo en desarrollo'
                      : null;

                    const handleClick = () => {
                      if (isDisabled) {
                        return;
                      }
                      handleNavigate(route.path);
                    };

                    const tileButton = (
                      <button
                        type="button"
                        onClick={handleClick}
                        style={tileStyles}
                        className="dashboard-sections__tile dashboard-focus-ring erp-overview__tile"
                        aria-label={`${route.title} (${copy.title})`}
                        aria-describedby={disabledReason ? `tile-reason-${route.key}` : undefined}
                        disabled={isDisabled}
                        data-path={route.path}
                        data-disabled={isDisabled ? 'true' : undefined}
                      >
                        {Icon ? (
                          <span
                            className="erp-overview__icon dashboard-sections__icon"
                            style={iconStyles}
                            aria-hidden="true"
                          >
                            <Icon />
                          </span>
                        ) : null}
                        <div className="erp-overview__tile-content">
                          <span className="erp-overview__title-text">{route.title}</span>
                          <div className="erp-overview__meta-row">
                            {isDisabled ? <Tag color="gold">En desarrollo</Tag> : null}
                          </div>
                        </div>
                      </button>
                    );

                    return (
                      <div className="erp-overview__tile-wrapper" key={route.key}>
                        {disabledReason ? (
                          <>
                            <Tooltip title={disabledReason} placement="top">
                              <span className="erp-overview__tile-tooltip">{tileButton}</span>
                            </Tooltip>
                            <span id={`tile-reason-${route.key}`} className="erp-overview__sr-only">
                              {disabledReason}
                            </span>
                          </>
                        ) : (
                          tileButton
                        )}
                        {disabledCopy ? (
                          <Typography.Text type="secondary" className="erp-overview__disabled-copy">
                            {disabledCopy}
                          </Typography.Text>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
};

export default ERPOverviewPage;
